/**
 * Lines on handicap / totals selections (Augustus, 26–27 Oct: "a handicap or
 * total without its number isn't a bettable market"; both sides shown).
 *
 * The 0053 /markets contract carries team names only. The line-aware contract
 * (Codex brief 2026-11-03-Lines) adds, per market, `line` (signed from the HOME
 * side for handicaps; the total for totals) and, per selection, `line` (signed
 * for that side) and `displayLabel` (composed server-side so the card, slip,
 * My bets and the settlement line all read identically). Until it lands every
 * field is optional and this module falls back to the bare label, so the page
 * is correct on either contract.
 *
 * Composition, when the server did not do it:
 *   Handicap  →  "Sydney Kings −1.5" / "Perth Wildcats +1.5"  (sign always shown)
 *   Totals    →  "Over 188.5" / "Under 188.5"
 */
import type { Market, Selection } from '@/types/api';

/**
 * Since the Step 7 contract (openapi.yaml) `Selection` and `Market` carry
 * `line` / `side` / `displayLabel` and `marketKey` / `line` natively; these
 * aliases are kept so call sites read as "line-aware" and so a pre-contract
 * object (tests, older boards) still type-checks — every added field is
 * optional in the schema.
 */
export type LineSelection = Selection;
export type LineMarket = Market;

/** `spreads#05b43911` → `spreads`: a line market's id carries a per-line hash after the key. */
export function baseMarketKey(key: string): string {
  const i = key.indexOf('#');
  return i === -1 ? key : key.slice(0, i);
}

export function marketKeyFromId(id: string): string {
  const i = id.indexOf(':');
  return i === -1 ? '' : baseMarketKey(id.slice(i + 1));
}

/** Unicode minus, one decimal only when the line has one: −1.5, +3, 188.5. */
export function formatLine(line: number, signed: boolean): string {
  const abs = Math.abs(line);
  const num = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  if (!signed) return num;
  if (line === 0) return 'pk';
  return `${line < 0 ? '−' : '+'}${num}`;
}

function homeTeamOf(market: Pick<Market, 'eventLabel'>): string | null {
  const [home] = market.eventLabel.split(/\s+vs\.?\s+/i).map((t) => t.trim());
  return home || null;
}

/** The signed line for one selection, or null when neither the selection nor the market carries one. */
export function selectionLine(sel: LineSelection, market: LineMarket): number | null {
  if (typeof sel.line === 'number') return sel.line;
  if (typeof market.line !== 'number') return null;
  const key = market.marketKey ?? marketKeyFromId(market.id);
  if (key === 'totals') return market.line;
  if (key === 'spreads') {
    const home = homeTeamOf(market);
    if (!home) return null;
    return sel.label.trim().toLowerCase() === home.toLowerCase() ? market.line : -market.line;
  }
  return null;
}

/** What the customer reads on the button, the slip, My bets and the settlement line. */
export function selectionDisplay(sel: LineSelection, market: LineMarket): string {
  if (sel.displayLabel) return sel.displayLabel;
  const line = selectionLine(sel, market);
  if (line === null) return sel.label;
  const key = market.marketKey ?? marketKeyFromId(market.id);
  if (key === 'totals') {
    const l = sel.label.trim().toLowerCase();
    const word = l.startsWith('over') ? 'Over' : l.startsWith('under') ? 'Under' : sel.label;
    return `${word} ${formatLine(line, false)}`;
  }
  return `${sel.label} ${formatLine(line, true)}`;
}

/**
 * Section heading for one market. Several handicap markets on one event differ
 * only by line, so the heading carries it: "Handicap · Kings −1.5" /
 * "Total points · 188.5". Lineless markets keep the plain label.
 */
export function marketHeading(market: LineMarket): string {
  const base = market.marketLabel || 'Market';
  if (typeof market.line !== 'number') return base;
  const key = market.marketKey ?? marketKeyFromId(market.id);
  if (key === 'totals') return `${base} · ${formatLine(market.line, false)}`;
  if (key === 'spreads') {
    const home = homeTeamOf(market);
    return home ? `${base} · ${home} ${formatLine(market.line, true)}` : `${base} · ${formatLine(market.line, true)}`;
  }
  return base;
}

/** Order line markets of the same key by their line so alternates read as a ladder. */
export function compareByLine(a: LineMarket, b: LineMarket): number {
  const la = typeof a.line === 'number' ? Math.abs(a.line) : Number.POSITIVE_INFINITY;
  const lb = typeof b.line === 'number' ? Math.abs(b.line) : Number.POSITIVE_INFINITY;
  return la - lb;
}
